import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import cartaoCreditoAnimation from '../assets/cartao-credito.json';
import { CreditCard, XmarkCircleSolid } from 'iconoir-react';
import { Modal, Button, Alert, Card } from 'react-bootstrap';
import { getCartoes, removerCartao } from '../services/api';
import { getCardBrandLogo, getCardBrandName } from '../utils/cardBrand';

function CartoesSalvosModal({ show, onHide, onSelectCard }) {
  const [cartoes, setCartoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show) {
      carregarCartoes();
    }
  }, [show]);

  const carregarCartoes = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('sitri_token');
      if (token) {
        const resultado = await getCartoes(token);
        if (resultado && resultado.success) {
          // A API retorna os cartões em resultado.data
          const cartoesData = resultado.data || [];
          
          // Transformar os dados para o formato esperado pelo modal
          const cartoesFormatados = cartoesData.map(cartao => ({
            id: cartao.id,
            numero: cartao.extra_info?.creditCardNumber || '****',
            bandeira: cartao.extra_info?.creditCardBrand || 'UNKNOWN',
            nome: 'Cartão de Crédito', // Nome padrão
            token: cartao.id // Usar o ID como token
          }));
          
          setCartoes(cartoesFormatados);
        } else {
          setError('Erro ao carregar cartões');
        }
      } else {
        setError('Token não encontrado');
      }
    } catch (error) {
      setError('Erro ao carregar cartões');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoverCartao = async (cartaoId) => {
    if (cartoes.length <= 1) {
      setError('Não é possível remover o último cartão');
      return;
    }
    
    try {
      const token = localStorage.getItem('sitri_token');
      const resultado = await removerCartao(token, cartaoId);
      if (resultado && resultado.success) {
        await carregarCartoes();
      } else {
        setError('Erro ao remover cartão');
      }
    } catch (error) {
      setError('Erro ao remover cartão');
    }
  };

  const handleSelecionarCartao = (cartao) => {
    onSelectCard(cartao);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Meus Cartões</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="py-2 mb-3">
            <small>{error}</small>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        ) : cartoes.length > 0 ? (
          <div>
            {cartoes.map((cartao) => {
              const detectedBrand = (cartao.bandeira && cartao.bandeira !== 'UNKNOWN') ? cartao.bandeira : 'UNKNOWN';
              const brandLogo = getCardBrandLogo(detectedBrand);
              const brandName = getCardBrandName(detectedBrand);
              
              return (
              <Card key={cartao.id} className="mb-2">
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-2" style={{gap:'12px'}}>
                        <div style={{width:'40px', height:'40px', display:'flex', alignItems:'center', justifyContent:'center'}}>
                          {brandLogo ? (
                            <img src={brandLogo} alt={brandName} style={{width:'32px', height:'auto'}} />
                          ) : (
                            <CreditCard className="text-primary" style={{fontSize: '1.5rem'}} />
                          )}
                        </div>
                        <div>
                          <strong>{brandName} **** {cartao.numero}</strong>
                          <div className="text-muted small">{cartao.nome}</div>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-1">
                      {onSelectCard && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleSelecionarCartao(cartao)}
                          style={{
                            backgroundColor: '#4a90e2',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '6px 16px',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}
                        >
                          Usar
                        </Button>
                      )}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoverCartao(cartao.id)}
                        disabled={cartoes.length <= 1}
                        title={cartoes.length <= 1 ? 'Não é possível remover o último cartão' : 'Remover cartão'}
                      >
                        <XmarkCircleSolid />
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4">
            <div style={{ width: '280px', height: '280px', margin: '0 auto' }}>
              <Lottie animationData={cartaoCreditoAnimation} loop={true} />
            </div>
            <div style={{ color: '#111827', fontWeight: 700, fontSize: '1rem' }}>Ops, nenhum cartão cadastrado por aqui</div>
            <div className="text-muted" style={{ fontSize: '0.9rem', marginBottom: '20px' }}>Cadastre um cartão de crédito para futuras compras.</div>
            <Button
              variant="primary"
              onClick={onHide}
              style={{
                backgroundColor: '#0039ff',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 24px',
                fontSize: '0.9rem',
                fontWeight: '600'
              }}
            >
              <CreditCard style={{ marginRight: '8px' }} />
              Adicionar Cartão
            </Button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CartoesSalvosModal;
